import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TimerCircle from "./TimerCircle";
import { ExerciseContext } from "../context/ExerciseContext";
import "../styles/TimerScreen.css";

const WORK_TIME = 20;
const REST_TIME = 10;
const TOTAL_ROUNDS = 8;
const BREAK_BETWEEN_SETS = 60;

const DEFAULT_EXERCISE = { name: "Unknown exercise", image: "/images/default.gif" };

const TimerScreen = () => {
    const navigate = useNavigate();
    const { selectedExercises } = useContext(ExerciseContext);

    const exercises = selectedExercises?.map(ex => ex || DEFAULT_EXERCISE) || [DEFAULT_EXERCISE];

    const [time, setTime] = useState(WORK_TIME);
    const [isRunning, setIsRunning] = useState(false);
    const [round, setRound] = useState(1);
    const [isWork, setIsWork] = useState(true);
    const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
    const [numSets, setNumSets] = useState(1);
    const [setCount, setSetCount] = useState(1);
    const [isBreakBetweenSets, setIsBreakBetweenSets] = useState(false);

    const [isSoundOn, setIsSoundOn] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    const startSoundRef = useRef(null);
    const endSoundRef = useRef(null);
    const timerRef = useRef(null);

    // Wake Lock reference
    const wakeLockRef = useRef(null);

    // Aktivace wake locku
    const requestWakeLock = async () => {
        try {
            if ("wakeLock" in navigator) {
                wakeLockRef.current = await navigator.wakeLock.request("screen");
                console.log("Wake Lock aktivní");

                wakeLockRef.current.addEventListener("release", () => {
                    console.log("Wake Lock uvolněn");
                });
            }
        } catch (err) {
            console.error("Nepodařilo se aktivovat Wake Lock:", err);
        }
    };

    // Uvolnění wake locku
    const releaseWakeLock = async () => {
        try {
            await wakeLockRef.current?.release();
            wakeLockRef.current = null;
            console.log("Wake Lock manuálně uvolněn");
        } catch (err) {
            console.error("Chyba při uvolnění Wake Lock:", err);
        }
    };

    // Fullscreen celé stránky
    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen()
                .then(() => setIsFullscreen(true))
                .catch(err => console.error(err));
        } else {
            document.exitFullscreen()
                .then(() => setIsFullscreen(false))
                .catch(err => console.error(err));
        }
    };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (isRunning) requestWakeLock();
        else releaseWakeLock();
        return () => releaseWakeLock();
    }, [isRunning]);

    useEffect(() => {
        if (!isRunning) return;

        const timer = setTimeout(() => {
            if (time === 0) {
                if (isBreakBetweenSets) {
                    setIsBreakBetweenSets(false);
                    setTime(WORK_TIME);
                    setIsWork(true);
                    setRound(1);
                    setCurrentExerciseIndex(0);
                    if (isSoundOn) startSoundRef.current?.play();
                    return;
                }

                if (round >= TOTAL_ROUNDS && !isWork) {
                    if (setCount < numSets) {
                        setSetCount(prev => prev + 1);
                        setIsBreakBetweenSets(true);
                        setTime(BREAK_BETWEEN_SETS);
                        return;
                    } else {
                        navigate("/summary", { state: { numSets } });
                        return;
                    }
                }

                setIsWork(prev => !prev);
                setTime(isWork ? REST_TIME : WORK_TIME);

                if (!isWork) {
                    setRound(prev => prev + 1);
                    setCurrentExerciseIndex(prev => (prev + 1) % exercises.length);
                }

            } else {
                setTime(prev => prev - 1);
            }

            if (isWork && time === WORK_TIME && isSoundOn) startSoundRef.current?.play();
            if (isWork && time === 1 && isSoundOn) endSoundRef.current?.play();

        }, 1000);

        return () => clearTimeout(timer);
    }, [time, isRunning, isWork, round, exercises, navigate, numSets, setCount, isBreakBetweenSets, isSoundOn]);

    const handleStartStop = () => setIsRunning(prev => !prev);
    const handleReset = () => {
        setIsRunning(false);
        setTime(WORK_TIME);
        setRound(1);
        setIsWork(true);
        setCurrentExerciseIndex(0);
        setSetCount(1);
        setIsBreakBetweenSets(false);
    };

    const currentExercise = exercises[currentExerciseIndex];

    return (
        <div className="exercise-selector-container">
            {/* Pozadí */}
            <img 
                src="/images/background.jpg" 
                alt="background" 
                className="bg-video" 
            />
            <div className="overlay"></div>

            {/* Logo */}
            <div className="logo-container">
                <a href="https://www.tabataworkout.online">
                    <img src="/images/logo.png" alt="Logo" className="logo" />
                </a>
            </div>

            {/* Přehled cviků */}
            {!isMobile && (
                <div className="exercise-list">
                    {exercises.map((ex, index) => (
                        <div 
                            key={index} 
                            className={`exercise-item 
                                ${index === currentExerciseIndex && isWork ? "active" : ""} 
                                ${index === (currentExerciseIndex + 1) % exercises.length && !isWork && round < TOTAL_ROUNDS ? "next" : ""}`}
                        >
                            {ex.videoTimer ? (
                                <video
                                    src={ex.videoTimer}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    className="exercise-thumb"
                                />
                            ) : (
                                <img src={ex.image} alt={ex.name} className="exercise-thumb" />
                            )}
                            <p>{ex.name}</p>
                        </div>
                    ))}
                </div>
            )}

            {/* Hlavní obsah */}
            <div ref={timerRef} className="timer-screen">
                <div className="main-content">
                    {!isMobile && (
                        <div className={`exercise-side ${!isWork || isBreakBetweenSets ? "hidden" : ""}`}>
                            {currentExercise.videoTimer ? (
                                <video
                                    src={currentExercise.videoTimer}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    className="exercise-image2"
                                />
                            ) : (
                                <img
                                    src={currentExercise.image}
                                    alt={currentExercise.name}
                                    className="exercise-image2"
                                />
                            )}
                            <p>{currentExercise.name}</p>
                        </div>
                    )}

                    <div className="timer-center">
                        <h2 className="exercise-title">{currentExercise.name}</h2>

                        <TimerCircle
                            time={time}
                            maxTime={isBreakBetweenSets ? BREAK_BETWEEN_SETS : (isWork ? WORK_TIME : REST_TIME)}
                            isWork={isWork && !isBreakBetweenSets}
                        />

                        {isMobile && !isBreakBetweenSets && isWork && (
                            currentExercise.videoTimer ? (
                                <video
                                    src={currentExercise.videoTimer}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    className="exercise-image2"
                                />
                            ) : (
                                <img
                                    src={currentExercise.image}
                                    alt={currentExercise.name}
                                    className="exercise-image2"
                                />
                            )
                        )}

                        <div className="session-info">
                            <div className="session-block">
                                <strong>
                                    {isBreakBetweenSets ? "Rest" : isWork ? "Exercise" : "Rest"}
                                </strong>
                                <span>Round {round} / {TOTAL_ROUNDS}</span>
                            </div>
                            <div className="session-block">
                                <strong>Set</strong>
                                <span>{setCount} / {numSets}</span>
                            </div>
                        </div>
                    </div>

                    {!isMobile && (
                        <div className={`exercise-side ${!isWork || isBreakBetweenSets ? "hidden" : ""}`}>
                            {currentExercise.videoTimer ? (
                                <video
                                    src={currentExercise.videoTimer}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    className="exercise-image2"
                                />
                            ) : (
                                <img
                                    src={currentExercise.image}
                                    alt={currentExercise.name}
                                    className="exercise-image2"
                                />
                            )}
                            <p>{currentExercise.name}</p>
                        </div>
                    )}
                </div>

                {/* Tlačítka */}
                <div className="buttons">
                    <div className="set-selection-inline">
                        <label htmlFor="numSets">Enter number of Tabata sets: </label>
                        <input
                            type="number"
                            id="numSets"
                            value={numSets}
                            min="1"
                            max="10"
                            onChange={(e) => setNumSets(Number(e.target.value))}
                            disabled={isRunning}
                        />
                    </div>

                    <button onClick={handleStartStop}>{isRunning ? "Stop" : "Start"}</button>
                    <button onClick={handleReset}>Reset</button>
                    <button onClick={() => setIsSoundOn(prev => !prev)}>
                        {isSoundOn ? "Mute" : "Unmute"}
                    </button>

                    {!isMobile && (
                        <button onClick={toggleFullscreen}>
                            {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                        </button>
                    )}

                    <button onClick={() => navigate("/")}>Back to Exercise Selection</button>
                </div>

                <audio ref={startSoundRef} src="/sounds/start-sound.mp3" />
                <audio ref={endSoundRef} src="/sounds/end-sound.mp3" />
            </div>
        </div>
    );
};

export default TimerScreen;
